/// <amd-module name="TFS/GlobalBanner/Components/GlobalDialog" />
import * as React from "react";

import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { ExternalContent } from "VSS/Ext/ExternalContent";

import { Link } from "VSSUI/Link";

import { GlobalMessagesChangedEvents, IGlobalDialog, IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService.Types";

interface IGlobalDialogComponentState {
    dialog?: IGlobalDialog;
}

export class GlobalDialogComponent extends VssComponent<{}, IGlobalDialogComponentState> {
    public static componentType = "globalDialog";

    private globalMessageService: IGlobalMessagesService;

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        this.globalMessageService = context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");

        const dialog = this.globalMessageService.getDialog();
        this.state = { dialog };
    }

    public componentDidMount() {
        this.globalMessageService.subscribe(this.onDialogChanged, GlobalMessagesChangedEvents.GlobalDialogChangedEvent);
    }

    public render(): JSX.Element | null {
        const { dialog } = this.state;

        if (!dialog) {
            return null;
        }

        const buttonProps = dialog.buttonProps || [];
        if (dialog.closeButtonProps) {
            buttonProps.push({ ...dialog.closeButtonProps, onClick: this.onCornerDialogClose });
        }

        return dialog.contributionId ? (
            <WrappedComponent
                wrappedType="customDialog"
                dependencies={["ms.vss-features.ui-layer-content"]}
                {...{
                    onDismiss: () => {
                        if (dialog.onDismiss) {
                            dialog.onDismiss();
                        }
                        this.onCornerDialogClose();
                    },
                    ...dialog.contentProperties
                }}
            >
                <ExternalContent
                    contributionId={dialog.contributionId}
                    initialHandshakeData={{
                        ...dialog.contributionConfiguration
                    }}
                />
            </WrappedComponent>
        ) : dialog.componentType ? (
            <WrappedComponent
                wrappedType={dialog.componentType}
                dependencies={dialog.contentDependencies}
                {...{ onDismiss: this.onCornerDialogClose, ...dialog.contentProperties }}
            />
        ) : (
            <WrappedComponent
                wrappedType="cornerDialog"
                dependencies={["ms.vss-features.ui-layer-content"]}
                onDismiss={this.onCornerDialogClose}
                footerButtonProps={buttonProps.length > 0 ? buttonProps : undefined}
                titleProps={{ text: dialog.title }}
            >
                {dialog.message || (dialog.messageFormat && dialog.messageLinks) ? (
                    dialog.messageFormat && dialog.messageLinks ? (
                        <FormatComponent className="message" format={dialog.messageFormat}>
                            {dialog.messageLinks.map((link, index) => (
                                <Link href={link.href} target="_blank" rel="noopener noreferrer" key={index}>
                                    {link.name}
                                </Link>
                            ))}
                        </FormatComponent>
                    ) : (
                        <div className="message">{dialog.message}</div>
                    )
                ) : null}
            </WrappedComponent>
        );
    }

    public componentWillUnmount() {
        this.globalMessageService.unsubscribe(this.onDialogChanged, GlobalMessagesChangedEvents.GlobalDialogChangedEvent);

        super.componentWillUnmount();
    }

    private onCornerDialogClose = (): void => {
        this.globalMessageService.closeDialog();
    };

    private onDialogChanged = (newDialog: IGlobalDialog | undefined): void => {
        this.setState({ dialog: newDialog });
    };
}

// Register the global corner dialog with the layout manager
VssComponent.register(GlobalDialogComponent.componentType, GlobalDialogComponent);
